import React, { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { 
  Home, Building2, Calendar, BookOpen, MessageSquare, Bell, 
  User, LogOut, Menu, X, Users, UserCheck, Shield, FileText
} from 'lucide-react';
import { useAuthStore } from '../store/authStore';
import { notificationService, messageService } from '../services/api';

interface DashboardLayoutProps {
  userType: 'gerant' | 'locataire' | 'admin';
}

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ userType }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [unreadNotifications, setUnreadNotifications] = useState(0);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuthStore();

  // Récupérer les compteurs de notifications non lues
  useEffect(() => {
    const fetchUnreadCounts = async () => {
      try {
        const [notifRes, msgRes] = await Promise.all([
          notificationService.getUnreadCount(),
          messageService.getUnreadCount()
        ]);
        setUnreadNotifications(notifRes.data.count || 0);
        setUnreadMessages(msgRes.data.count || 0);
      } catch (error) {
        console.error('Error fetching unread counts:', error);
      }
    };

    fetchUnreadCounts();
    // Actualiser toutes les 30 secondes
    const interval = setInterval(fetchUnreadCounts, 30000);
    return () => clearInterval(interval);
  }, [location.pathname]); // Rafraîchir quand on change de page

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Navigation selon le type d'utilisateur
  const getNavigation = () => {
    if (userType === 'admin') {
      return [
        { name: 'Tableau de bord', href: '/admin', icon: Home, badge: 0 },
        { name: 'Gérants', href: '/admin/gerants', icon: UserCheck, badge: 0 },
        { name: 'Locataires', href: '/admin/locataires', icon: Users, badge: 0 },
        { name: 'Appartements', href: '/admin/apartments', icon: Building2, badge: 0 },
        { name: 'Réservations', href: '/admin/bookings', icon: BookOpen, badge: 0 },
        { name: 'Factures', href: '/admin/invoices', icon: FileText, badge: 0 },
        { name: 'Calendrier', href: '/admin/calendar', icon: Calendar, badge: 0 },
        { name: 'Messages', href: '/admin/messages', icon: MessageSquare, badge: 0 },
        { name: 'Notifications', href: '/admin/notifications', icon: Bell, badge: 0 },
      ];
    }
    if (userType === 'gerant') {
      return [
        { name: 'Tableau de bord', href: '/gerant', icon: Home, badge: 0 },
        { name: 'Mes appartements', href: '/gerant/apartments', icon: Building2, badge: 0 },
        { name: 'Calendrier', href: '/gerant/calendar', icon: Calendar, badge: 0 },
        { name: 'Réservations', href: '/gerant/bookings', icon: BookOpen, badge: 0 },
        { name: 'Factures', href: '/gerant/invoices', icon: FileText, badge: 0 },
        { name: 'Messages', href: '/gerant/messages', icon: MessageSquare, badge: unreadMessages },
        { name: 'Notifications', href: '/gerant/notifications', icon: Bell, badge: unreadNotifications },
      ];
    }
    return [
      { name: 'Accueil', href: '/locataire', icon: Home, badge: 0 },
      { name: 'Mes réservations', href: '/locataire/bookings', icon: BookOpen, badge: 0 },
      { name: 'Messages', href: '/locataire/messages', icon: MessageSquare, badge: unreadMessages },
      { name: 'Notifications', href: '/locataire/notifications', icon: Bell, badge: unreadNotifications },
    ];
  };

  const navigation = getNavigation();

  const isActive = (path: string) => {
    if (path === '/gerant' || path === '/locataire' || path === '/admin') {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-neutral-100">
      {/* Sidebar Mobile Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 z-40 bg-black bg-opacity-50 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out
        lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-6 border-b border-neutral-200">
          <Link to="/" className="flex items-center space-x-2">
            <div className="w-8 h-8 bg-primary-500 rounded-lg flex items-center justify-center">
              <Building2 className="w-5 h-5 text-white" />
            </div>
            <span className="font-heading font-semibold text-lg text-neutral-800">Gadaye</span>
          </Link>
          <button 
            onClick={() => setSidebarOpen(false)}
            className="lg:hidden p-2 rounded-lg hover:bg-neutral-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
          {navigation.map((item) => {
            const Icon = item.icon;
            const active = isActive(item.href);
            return (
              <Link
                key={item.name}
                to={item.href}
                className={`
                  flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors
                  ${active 
                    ? 'bg-primary-50 text-primary-700' 
                    : 'text-neutral-600 hover:bg-neutral-100 hover:text-neutral-900'}
                `}
                onClick={() => setSidebarOpen(false)}
              >
                <div className="flex items-center">
                  <Icon className={`w-5 h-5 mr-3 ${active ? 'text-primary-500' : ''}`} />
                  {item.name}
                </div>
                {item.badge > 0 && (
                  <span className="bg-primary-500 text-white text-xs px-2 py-0.5 rounded-full min-w-[20px] text-center">
                    {item.badge > 99 ? '99+' : item.badge}
                  </span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* User Section */}
        <div className="border-t border-neutral-200 p-4">
          <Link 
            to={`/${userType}/profile`}
            className="flex items-center p-3 rounded-lg hover:bg-neutral-100 transition-colors"
          >
            <div className="w-10 h-10 bg-primary-100 rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-primary-600" />
            </div>
            <div className="ml-3 flex-1 min-w-0">
              <p className="text-sm font-medium text-neutral-900 truncate">
                {user?.prenom} {user?.nom}
              </p>
              <p className="text-xs text-neutral-500 truncate">{user?.email}</p>
            </div>
          </Link>
          <button
            onClick={handleLogout}
            className="mt-2 w-full flex items-center px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
          >
            <LogOut className="w-4 h-4 mr-2" />
            Déconnexion
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:pl-64">
        {/* Top Header */}
        <header className="sticky top-0 z-30 bg-white border-b border-neutral-200">
          <div className="flex items-center justify-between h-16 px-4 lg:px-8">
            {/* Mobile menu button */}
            <button 
              onClick={() => setSidebarOpen(true)}
              className="lg:hidden p-2 rounded-lg hover:bg-neutral-100"
            >
              <Menu className="w-6 h-6" />
            </button>

            {/* Page title */}
            <h1 className="text-lg font-semibold text-neutral-800 lg:text-xl">
              {userType === 'admin' ? 'Administration' : userType === 'gerant' ? 'Espace Gérant' : 'Espace Locataire'}
            </h1>

            {/* Right actions */}
            <div className="flex items-center space-x-4">
              <Link 
                to={`/${userType}/messages`}
                className="relative p-2 rounded-lg hover:bg-neutral-100"
                title="Messages"
              >
                <MessageSquare className="w-5 h-5 text-neutral-600" />
                {unreadMessages > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-primary-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {unreadMessages > 9 ? '9+' : unreadMessages}
                  </span>
                )}
              </Link>
              <Link 
                to={`/${userType}/notifications`}
                className="relative p-2 rounded-lg hover:bg-neutral-100"
                title="Notifications"
              >
                <Bell className="w-5 h-5 text-neutral-600" />
                {unreadNotifications > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 bg-red-500 text-white text-xs w-5 h-5 rounded-full flex items-center justify-center">
                    {unreadNotifications > 9 ? '9+' : unreadNotifications}
                  </span>
                )}
              </Link>
              <Link 
                to="/"
                className="hidden sm:inline-flex btn btn-outline btn-sm"
              >
                Voir le site
              </Link>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default DashboardLayout;
